// Afterimage engine: soft objects whose moving parts leave a fan of rainbow
// afterimages. WebGL2, no libraries. Ported from Alex's "afterimage" and Ask
// Opus orb experiments; the canvas is transparent so the orb sits on any
// surface. Typed in engine.d.ts.
//
// An object is a set of parts. A part is a "pillow": a flat outline puffed
// into a soft solid, placed by a matrix and optionally bent around a hinge.
// The trail is a fan of copies of the moving parts, each posed part of the
// way between the rest pose and where the object was a moment ago.

// ---------- matrices (column-major) ----------
export const m4 = {
  I: () => new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]),
  mul(...ms) {
    return ms.reduce((a, b) => {
      const o = new Float32Array(16);
      for (let c = 0; c < 4; c++)
        for (let r = 0; r < 4; r++) {
          let s = 0;
          for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
          o[c * 4 + r] = s;
        }
      return o;
    });
  },
  T(x = 0, y = 0, z = 0) {
    const o = m4.I();
    o[12] = x;
    o[13] = y;
    o[14] = z;
    return o;
  },
  S(x = 1, y = x, z = x) {
    const o = m4.I();
    o[0] = x;
    o[5] = y;
    o[10] = z;
    return o;
  },
  RX(a) {
    const c = Math.cos(a),
      s = Math.sin(a);
    return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]);
  },
  RY(a) {
    const c = Math.cos(a),
      s = Math.sin(a);
    return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]);
  },
  RZ(a) {
    const c = Math.cos(a),
      s = Math.sin(a);
    return new Float32Array([c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  },
  persp(fovy, asp, n, f) {
    const t = 1 / Math.tan(fovy / 2);
    return new Float32Array([
      t / asp,
      0,
      0,
      0,
      0,
      t,
      0,
      0,
      0,
      0,
      (f + n) / (n - f),
      -1,
      0,
      0,
      (2 * f * n) / (n - f),
      0,
    ]);
  },
};
export const rad = (d) => (d * Math.PI) / 180;

// ---------- time helpers ----------
export const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
export const seg = (t, a, b) => clamp((t - a) / (b - a));
export const smooth = (t, a, b) => {
  const x = seg(t, a, b);
  return x * x * (3 - 2 * x);
};
export const mix = (a, b, t) => a + (b - a) * t;
export const easeOut = (x, p = 3) => 1 - Math.pow(1 - x, p);
export const easeIn = (x, p = 3) => Math.pow(x, p);
export const easeInOut = (x, p = 3) =>
  x < 0.5 ? Math.pow(2 * x, p) / 2 : 1 - Math.pow(2 - 2 * x, p) / 2;

// ---------- outlines ----------
// Closed Catmull-Rom through control points.
export function spline(pts, per = 14) {
  const n = pts.length,
    out = [];
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i + n - 1) % n],
      p1 = pts[i],
      p2 = pts[(i + 1) % n],
      p3 = pts[(i + 2) % n];
    for (let k = 0; k < per; k++) {
      const t = k / per,
        t2 = t * t,
        t3 = t2 * t;
      out.push(
        [0, 1].map(
          (d) =>
            0.5 *
            (2 * p1[d] +
              (-p0[d] + p2[d]) * t +
              (2 * p0[d] - 5 * p1[d] + 4 * p2[d] - p3[d]) * t2 +
              (-p0[d] + 3 * p1[d] - 3 * p2[d] + p3[d]) * t3),
        ),
      );
    }
  }
  return out;
}
// Polygon with rounded corners; r is one radius or one per corner.
export function roundPoly(pts, r, steps = 8) {
  const n = pts.length,
    out = [];
  for (let i = 0; i < n; i++) {
    const P = pts[i],
      A = pts[(i + n - 1) % n],
      B = pts[(i + 1) % n];
    let ux = A[0] - P[0],
      uy = A[1] - P[1],
      vx = B[0] - P[0],
      vy = B[1] - P[1];
    const lu = Math.hypot(ux, uy),
      lv = Math.hypot(vx, vy);
    ux /= lu;
    uy /= lu;
    vx /= lv;
    vy /= lv;
    const half = Math.acos(clamp(ux * vx + uy * vy, -1, 1)) / 2;
    const rr = Array.isArray(r) ? r[i] : r;
    if (!rr || half > 1.56) {
      out.push(P);
      continue;
    }
    const d = Math.min(rr / Math.tan(half), lu * 0.5, lv * 0.5),
      rad = d * Math.tan(half);
    let bx = ux + vx,
      by = uy + vy;
    const lb = Math.hypot(bx, by);
    bx /= lb;
    by /= lb;
    const cx = P[0] + (bx * rad) / Math.sin(half),
      cy = P[1] + (by * rad) / Math.sin(half);
    const a0 = Math.atan2(P[1] + uy * d - cy, P[0] + ux * d - cx);
    let da = Math.atan2(P[1] + vy * d - cy, P[0] + vx * d - cx) - a0;
    while (da > Math.PI) da -= 2 * Math.PI;
    while (da < -Math.PI) da += 2 * Math.PI;
    for (let k = 0; k <= steps; k++)
      out.push([
        cx + rad * Math.cos(a0 + (da * k) / steps),
        cy + rad * Math.sin(a0 + (da * k) / steps),
      ]);
  }
  return out;
}
export const rrect = (w, h, r, cx = 0, cy = 0) =>
  roundPoly(
    [
      [cx - w / 2, cy - h / 2],
      [cx + w / 2, cy - h / 2],
      [cx + w / 2, cy + h / 2],
      [cx - w / 2, cy + h / 2],
    ],
    r,
  );
export const ellipse = (rx, ry = rx, cx = 0, cy = 0, n = 72) =>
  Array.from({ length: n }, (_, i) => [
    cx + rx * Math.cos((i / n) * 2 * Math.PI),
    cy + ry * Math.sin((i / n) * 2 * Math.PI),
  ]);

const area = (p) =>
  p.reduce((s, a, i) => {
    const b = p[(i + 1) % p.length];
    return s + a[0] * b[1] - b[0] * a[1];
  }, 0) / 2;
function centroid(p) {
  let x = 0,
    y = 0,
    A = 0;
  p.forEach((a, i) => {
    const b = p[(i + 1) % p.length],
      c = a[0] * b[1] - b[0] * a[1];
    x += (a[0] + b[0]) * c;
    y += (a[1] + b[1]) * c;
    A += c;
  });
  return [x / (3 * A), y / (3 * A)];
}
function resample(p, n) {
  const len = [0];
  for (let i = 0; i < p.length; i++) {
    const a = p[i],
      b = p[(i + 1) % p.length];
    len.push(len[i] + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  const total = len[p.length],
    out = [];
  let j = 0;
  for (let i = 0; i < n; i++) {
    const d = (i / n) * total;
    while (len[j + 1] < d) j++;
    const a = p[j],
      b = p[(j + 1) % p.length],
      t = (d - len[j]) / Math.max(1e-9, len[j + 1] - len[j]);
    out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
  }
  return out;
}

// ---------- the pillow ----------
// Rings shrink toward the centre while the surface rises to +/-T, so the rim
// is round and the middle is flat. `a` sets how flat (higher = flatter).
// The normals are not the surface's own: they lean out toward the rim as if
// the shape were much fatter, so a thin sheet still catches light like
// something soft. `p` sets how far in the lean reaches (lower = rounder).
// Returns raw arrays; the renderer uploads them on first use.
export function pillow(
  outline,
  { c, T = 0.03, a = 2.6, p = 2.4, N = 112, M = 10, z = 0 } = {},
) {
  let o = outline;
  if (area(o) < 0) o = o.slice().reverse();
  o = resample(o, N);
  c = c || centroid(o);
  // Outward direction along the outline, eased round the corners.
  let out = o.map((_, i) => {
    const A = o[(i + N - 1) % N],
      B = o[(i + 1) % N],
      l = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
    return [(B[1] - A[1]) / l, -(B[0] - A[0]) / l];
  });
  for (let k = 0; k < 3; k++)
    out = out.map((n, i) =>
      [0, 1].map(
        (d) => out[(i + N - 1) % N][d] + 2 * n[d] + out[(i + 1) % N][d],
      ),
    );
  out = out.map((n) => {
    const l = Math.hypot(n[0], n[1]) || 1;
    return [n[0] / l, n[1] / l];
  });
  const V = [];
  const ring = (j, sign) => {
    const psi = ((Math.PI / 2) * j) / M,
      s = Math.pow(Math.sin(psi), 2 / a),
      h = T * Math.cos(psi);
    const lean = (Math.PI / 2) * Math.pow(s, p),
      ls = Math.sin(lean),
      lc = Math.cos(lean);
    // Further in, the rings are small: blend the lean over more neighbours
    // so corners of the outline don't draw creases to the centre.
    let dir = out;
    for (let k = Math.min(400, Math.round(8 / (s * s))); k > 0; k--)
      dir = dir.map((n, i) =>
        [0, 1].map(
          (d) => (dir[(i + N - 1) % N][d] + 2 * n[d] + dir[(i + 1) % N][d]) / 4,
        ),
      );
    for (let i = 0; i < N; i++) {
      const l = Math.hypot(dir[i][0], dir[i][1]) || 1;
      V.push(
        c[0] + (o[i][0] - c[0]) * s,
        c[1] + (o[i][1] - c[1]) * s,
        z + sign * h,
        (dir[i][0] / l) * ls,
        (dir[i][1] / l) * ls,
        sign * lc,
        s,
      );
    }
  };
  V.push(c[0], c[1], z + T, 0, 0, 1, 0);
  for (let j = 1; j <= M; j++) ring(j, 1);
  const backC = V.length / 7;
  V.push(c[0], c[1], z - T, 0, 0, -1, 0);
  for (let j = 1; j < M; j++) ring(j, -1);
  const F = (j, i) => 1 + (j - 1) * N + (i % N);
  const B = (j, i) => (j === M ? F(M, i) : backC + 1 + (j - 1) * N + (i % N));
  const idx = [];
  for (let i = 0; i < N; i++) {
    idx.push(0, F(1, i), F(1, i + 1), backC, B(1, i + 1), B(1, i));
    for (let j = 1; j < M; j++) {
      idx.push(
        F(j, i),
        F(j + 1, i),
        F(j + 1, i + 1),
        F(j, i),
        F(j + 1, i + 1),
        F(j, i + 1),
      );
      idx.push(
        B(j, i),
        B(j + 1, i + 1),
        B(j + 1, i),
        B(j, i),
        B(j, i + 1),
        B(j + 1, i + 1),
      );
    }
  }
  return {
    data: new Float32Array(V),
    idx: new Uint16Array(idx),
    count: idx.length,
  };
}

// ---------- the ribbon ----------
// A soft stroke along an open path, with round ends: for shapes a pillow
// can't make, like an arc.
export function ribbon(path, w, { T = 0.03, K = 30, J = 7, z = 0 } = {}) {
  const r = w / 2,
    ext = (a, b) => {
      const l = Math.hypot(a[0] - b[0], a[1] - b[1]) || 1;
      return [a[0] + ((a[0] - b[0]) / l) * r, a[1] + ((a[1] - b[1]) / l) * r];
    };
  const full = [
    ext(path[0], path[1]),
    ...path,
    ext(path[path.length - 1], path[path.length - 2]),
  ];
  const len = [0];
  for (let i = 1; i < full.length; i++)
    len.push(
      len[i - 1] +
        Math.hypot(full[i][0] - full[i - 1][0], full[i][1] - full[i - 1][1]),
    );
  const L = len[len.length - 1],
    at = (d) => {
      let i = 1;
      while (i < full.length - 1 && len[i] < d) i++;
      const t = (d - len[i - 1]) / Math.max(1e-9, len[i] - len[i - 1]);
      return [
        mix(full[i - 1][0], full[i][0], t),
        mix(full[i - 1][1], full[i][1], t),
      ];
    };
  const V = [],
    idx = [];
  for (const sign of [1, -1]) {
    const base = V.length / 7;
    for (let k = 0; k < K; k++) {
      const d = (k / (K - 1)) * L,
        p = at(d),
        a = at(Math.max(0, d - L / K / 2)),
        b = at(Math.min(L, d + L / K / 2));
      const tl = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1,
        n = [-(b[1] - a[1]) / tl, (b[0] - a[0]) / tl];
      // The half-width closes to nothing over the last r of each end.
      const e = Math.min(d, L - d),
        hw = e >= r ? r : Math.sqrt(Math.max(0, r * r - (r - e) * (r - e)));
      for (let j = 0; j < J; j++) {
        const u = Math.sin((Math.PI / 2) * ((j / (J - 1)) * 2 - 1)),
          c = Math.sqrt(Math.max(0, 1 - u * u));
        V.push(
          p[0] + n[0] * hw * u,
          p[1] + n[1] * hw * u,
          z + (sign * T * c * hw) / r,
          n[0] * u,
          n[1] * u,
          sign * c,
          Math.max(Math.abs(u), 1 - hw / r),
        );
      }
    }
    for (let k = 0; k < K - 1; k++)
      for (let j = 0; j < J - 1; j++) {
        const q = base + k * J + j,
          tri = [q, q + J, q + J + 1, q, q + J + 1, q + 1];
        idx.push(...(sign > 0 ? tri : tri.reverse()));
      }
  }
  return {
    data: new Float32Array(V),
    idx: new Uint16Array(idx),
    count: idx.length,
  };
}

// ---------- the fan of afterimages ----------
// Copies of the moving parts, each posed part of the way (`gain`) between the
// rest pose and where the object was a moment ago, each in its own colour.
// Drawn in this order, so the oldest lies on top: lime nearest the object,
// then blue, red and yellow, and last the warm ghost of the rest pose.
// More copies (for thin parts, like a clock hand) fill in the same spectrum.
const SPECTRUM = [
  [0.99, 0.92, 0.22],
  [1, 0.58, 0.14],
  [1, 0.22, 0.18],
  [0.92, 0.16, 0.9],
  [0.01, 0.49, 1],
  [0.25, 0.82, 1],
  [0.84, 0.97, 0.21],
];
const GAINS = [0.08, 0.16, 0.46, 0.8];
const along = (stops, u) => {
  const x = clamp(u) * (stops.length - 1),
    i = Math.min(stops.length - 2, Math.floor(x));
  return Array.isArray(stops[i])
    ? stops[i].map((v, k) => mix(v, stops[i + 1][k], x - i))
    : mix(stops[i], stops[i + 1], x - i);
};
// `gains` are stops from the copy nearest rest to the one nearest the object;
// [.1, .9] spaces them evenly.
export function fan(n = 4, gains = GAINS) {
  const list = [];
  for (let i = n - 1; i >= 0; i--) {
    const u = i / (n - 1);
    // Blue shades to cyan at its rim; the rest keep one colour.
    list.push({
      gain: along(gains, u),
      c: along(SPECTRUM, u),
      rim: along(SPECTRUM, u > 0.5 ? u + 1 / 6 : u),
      fade: [mix(0.16, 0, u), mix(0.26, 0.12, u)],
    });
  }
  list.push({ gain: 0.035, ghost: true });
  return list;
}
const ECHOES = fan(4);

const VERT = `#version 300 es
layout(location=0) in vec3 aPos;
layout(location=1) in vec3 aNor;
layout(location=2) in float aS;
uniform mat4 uProj, uRoot, uPart;
uniform vec2 uBend; // angle at the hinge, curl per unit length
out vec3 vN, vO;
out float vS;
void main() {
  // The hinge is the local Y axis. A point x away from it rides an arc.
  float x = max(aPos.x, 0.), th = uBend.x + uBend.y * x;
  float c0 = cos(uBend.x), s0 = sin(uBend.x), c = cos(th), s = sin(th);
  vec2 arc = abs(uBend.y) < 1e-4 ? x * vec2(c0, s0) : vec2(s - s0, c0 - c) / uBend.y;
  arc += min(aPos.x, 0.) * vec2(c0, s0);
  vec3 p = vec3(arc.x - aPos.z * s, aPos.y, arc.y + aPos.z * c);
  vec3 n = vec3(aNor.x * c - aNor.z * s, aNor.y, aNor.x * s + aNor.z * c);
  vec4 o = uPart * vec4(p, 1.);
  vO = o.xyz;
  vN = mat3(uRoot) * mat3(uPart) * n;
  vS = aS;
  gl_Position = uProj * uRoot * o;
}`;

// The warm side of the afterimage: yellow at the heart of the object, running
// through pink to violet at its far tips. r is the distance on screen.
const WARM = `
vec3 ramp(float t, vec3 a, vec3 b, float t0, float t1) { return mix(a, b, clamp((t - t0) / (t1 - t0), 0., 1.)); }
vec3 warm(float r) {
  vec3 c = vec3(1., .88, .54);
  c = ramp(r, c, vec3(1., .82, .48), .00, .17);
  c = ramp(r, c, vec3(1., .52, .44), .17, .31);
  c = ramp(r, c, vec3(1., .30, .86), .31, .48);
  c = ramp(r, c, vec3(.96, .12, .98), .48, .58);
  c = ramp(r, c, vec3(.73, .0, 1.), .58, .72);
  c = ramp(r, c, vec3(.54, .0, 1.), .72, .95);
  return c;
}`;

// The Ask Opus fill: #859CFD core, #ADBDFF mid, #FFE276 gold edge, #072EAD
// shading. Other orbs may bring their own stops (Renderer.fill).
export const ASK_OPUS_FILL = {
  core: [0.522, 0.612, 0.992],
  mid: [0.678, 0.741, 1],
  edge: [1, 0.886, 0.463],
  deep: [0.027, 0.18, 0.678],
};

// The live object: sky blue at the top running to deep blue at the foot,
// with light catching whatever faces up.
const LIVE = `#version 300 es
precision highp float;
in vec3 vN, vO;
in float vS;
uniform vec3 uGrad;  // y of deep blue, y of sky blue, tone (lifts toward white)
uniform vec4 uCore;  // x, y, radius, strength of the deep-blue heart
uniform vec4 uGlow;  // x, y, radius, strength of the pale haze
uniform vec4 uTint;  // heart on screen (x, y), one unit in px, how much colour clings to the part
uniform float uBrand; // 0: the original sky blue, 1: the Ask Opus blue and gold
uniform vec3 uWhirl;  // amount, phase, part tone (eyes stay white)
uniform float uPearl; // 0..1: the agent bots' white orb over whatever uBrand drew
uniform vec3 uPearlTint; // the pearl's body colour (--color-agent-orb, or a tint)
uniform vec3 uPearlEdge; // the edge as a factor of the body (--color-agent-orb-edge ÷ orb)
uniform vec3 uPearlEye;  // the eyes on it (--color-agent-orb-eye)
uniform vec3 uFillCore, uFillMid, uFillEdge, uFillDeep; // the fill's stops (Ask Opus's by default)
uniform vec4 uTex;   // texture on the fill: grain, mottle, specks, seed (all 0: smooth, as Ask Opus)
uniform vec3 uSpeck; // the specks' colour
uniform float uTexT; // seconds, to drift the texture (0: held still)
uniform float uWobble; // 0: a round edge; up to 1: an edge that moves like a soft blob
uniform vec3 uDisc;   // this part's centre (x, y) and radius, object space
uniform vec2 uSpin;   // longitude the ball has turned to, and how strongly to show it
out vec4 o;
${WARM}
// Value noise for the textured fills (a grainy photo look).
float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3. - 2. * f);
  // The lattice repeats every 64 cells, so a drift wrapped by 64 lands on
  // the same pattern (no jump) and the hash's input stays small and exact.
  vec2 a = mod(i, 64.), b = mod(i + 1., 64.);
  return mix(mix(hash21(a), hash21(vec2(b.x, a.y)), u.x), mix(hash21(vec2(a.x, b.y)), hash21(b), u.x), u.y);
}
// Whole-number octaves keep the 64-cell period at every octave.
float fbm(vec2 p) { return .55 * vnoise(p) + .3 * vnoise(p * 2. + 7.1) + .15 * vnoise(p * 4. + 3.7); }
// Ask Opus: deep #072EAD at the heart, #859CFD core, #ADBDFF mid, then white.
vec3 brand(float t) {
  vec3 a = vec3(.027, .18, .68), b = vec3(.30, .40, .93), c = vec3(.52, .61, .99);
  vec3 d = vec3(.62, .69, 1.), e = vec3(.72, .78, 1.), f = vec3(.80, .85, 1.);
  t = clamp(t, 0., 1.6);
  if (t < .3) return mix(a, b, t / .3);
  if (t < .62) return mix(b, c, (t - .3) / .32);
  if (t < 1.) return mix(c, d, (t - .62) / .38);
  if (t < 1.3) return mix(d, e, (t - 1.) / .3);
  return mix(e, f, (t - 1.3) / .3);
}
vec3 blue(float t) {
  vec3 a = vec3(0., .12, 1.), b = vec3(0., .31, 1.), c = vec3(.02, .52, 1.);
  vec3 d = vec3(.22, .75, .98), e = vec3(.62, .85, .97), f = vec3(.90, .95, .99);
  t = clamp(t, 0., 1.6);
  if (t < .3) return mix(a, b, t / .3);
  if (t < .62) return mix(b, c, (t - .3) / .32);
  if (t < 1.) return mix(c, d, (t - .62) / .38);
  if (t < 1.3) return mix(d, e, (t - 1.) / .3);
  return mix(e, f, (t - 1.3) / .3);
}
void main() {
  vec3 n = normalize(vN);
  float t = .3 + .7 * smoothstep(uGrad.x, uGrad.y, vO.y) + uGrad.z;
  t -= uCore.w * exp(-dot(vO.xy - uCore.xy, vO.xy - uCore.xy) / (uCore.z * uCore.z));
  t += uGlow.w * exp(-dot(vO.xy - uGlow.xy, vO.xy - uGlow.xy) / (uGlow.z * uGlow.z));
  float up = smoothstep(.15, .95, n.y);
  t += .62 * up * up + .1 * smoothstep(.0, 1., n.y);
  t -= .12 * smoothstep(.2, 1., -n.y);
  t += .16 * pow(1. - abs(n.z), 2.) * (.5 + .5 * n.y);
  vec3 c = blue(t);
  float alpha = 1.;
  if (uBrand > .5) { // 1: Ask Opus fill; 2: black (graphite with uWhirl.x); between: a blend
    // The Ask Opus fill, as in the original orb: radial #859CFD core -> #ADBDFF
    // at 55% -> #FFE276 gold at the edge, a gold inner rim (screen), #072EAD
    // shading toward the foot (overlay) and a gloss up and to the left.
    // Ask Opus's stops unless an orb brings its own (renderer.fill).
    vec3 core = uFillCore, mid = uFillMid, gold = uFillEdge, deep = uFillDeep;
    vec2 q = (vO.xy - uDisc.xy) / uDisc.z;
    float d = length(q), g = clamp(d / 1.079, 0., 1.);
    // Straight blue to gold, as in the original orb, eased so the stops don't show.
    vec3 b = mix(mix(core, mid, smoothstep(0., .55, g)), gold, smoothstep(.55, 1., g));
    b = 1. - (1. - b) * (1. - gold * exp(-max(1. - d, 0.) / .077) * .666);
    float py = -q.y;
    vec3 ov = mix(2. * b * deep, 1. - 2. * (1. - b) * (1. - deep), step(vec3(.5), b));
    // Deep-blue shading toward the foot, eased off over the gold so it stays gold.
    b = mix(b, ov, clamp(.543 + .492 * py + .121 * py * py, 0., .851) * .92);
    // Spin: surface detail mapped to real sphere longitude/latitude, so it
    // compresses toward the limb and wraps, plus limb darkening (as in the
    // original orb). The gradient itself stays put, like light on a ball.
    if (uSpin.y > .001) {
      float nz = sqrt(max(1. - dot(q, q), 1e-4));
      float lo = atan(q.x, nz) + uSpin.x, la = asin(clamp(q.y, -1., 1.));
      float bnd = .62 * sin(2. * lo + .5 * sin(2. * la)) + .38 * sin(4. * lo + 1.7 + .8 * la);
      bnd *= smoothstep(.0, .35, nz); // fade where the sphere's depth runs out, so the poles don't pinch
      b *= 1. + uSpin.y * .22 * bnd;
      b = mix(b, gold, uSpin.y * .18 * max(bnd, 0.));
      b *= 1. - uSpin.y * .13 * (1. - nz);
    }
    if (uTex.x + uTex.y + uTex.z > 0.) {
      // A textured fill that flows: noise folded through itself, with time
      // in the fold, so the colour drifts and curls like ink. The twist
      // rocks back and forth and the whole turns slowly; nothing that grows
      // with time depends on the radius, so it never winds into a spiral.
      // It mottles light and dark, swirls the rim colour through the body,
      // carries soft specks of an accent colour, and sits under fine film
      // grain fixed to the screen.
      // Every term that grows with time is wrapped onto a period it repeats
      // on (a turn for angles, the noise's 64 cells for drifts), so an orb
      // left on screen for hours flows exactly as it did in its first minute.
      float t = uTexT, r = length(q), turn = 6.2831853;
      float core = 1. - smoothstep(0., 1., r);
      float ang = mod(.09 * t, turn) + (1.1 * sin(mod(.21 * t, turn)) + .6 * sin(mod(.083 * t, turn) + 1.3)) * core;
      vec2 w = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * q;
      vec2 o = vec2(fbm(w * 1.4 + mod(vec2(.22, .08) * t, 64.) + uTex.w), fbm(w * 1.4 + vec2(5.2, 0.) + mod(vec2(-.12, .18) * t, 64.) + uTex.w));
      w += 1.05 * (o - .5);
      vec2 o2 = vec2(fbm(w * 2.2 + vec2(1.7, 0.) + mod(vec2(0., -.16) * t, 64.)), fbm(w * 2.2 + vec2(9.2, 2.) + mod(vec2(.14, 0.) * t, 64.)));
      w += .5 * (o2 - .5);
      float m = fbm(w * 2.6 + uTex.w);
      b *= 1. - uTex.y * (m - .45) * 1.3;
      b = mix(b, uFillEdge, uTex.y * .9 * smoothstep(.5, .78, fbm(w * 1.9 + 3.3 + uTex.w)));
      float sp = smoothstep(.62, .8, fbm(w * 5. + uTex.w * 1.7 + 11.)); // soft, blurry flecks
      b = mix(b, uSpeck, uTex.z * sp);
      b += uTex.x * (hash21(gl_FragCoord.xy * .37 + uTex.w) - .5);
      b = clamp(b, 0., 1.);
    }
    vec2 hp = (q - vec2(-.34, .48)) * vec2(1., 1.6);
    b = mix(b, vec3(1.), .72 * exp(-dot(hp, hp) / .03));
    // Keep the pillow's soft light so it still reads as a solid.
    b = mix(b, vec3(1.), .05 * up * up);
    b *= 1. - .05 * smoothstep(.2, 1., -n.y);
    if (uBrand > 1.001) {
      // The agent bots: glossy black, lit softly from above, the same gloss.
      // uWhirl.x lifts it to graphite and adds a faint Ask Opus gold rim.
      // uBrand between 1 and 2 blends from the Ask Opus fill (1) to this (2).
      float lift = uWhirl.x;
      vec3 k = mix(vec3(.20, .20, .23), vec3(.07, .07, .09), smoothstep(.1, 1., g));
      k = mix(k, vec3(.36, .37, .41), lift);
      k = mix(k, vec3(.42, .42, .46) + .15 * lift, .35 * up * up);
      k *= 1. - .25 * smoothstep(.2, 1., -n.y);
      k = mix(k, vec3(.55, .55, .6), .25 * pow(1. - abs(n.z), 3.)); // a faint rim of light
      k = 1. - (1. - k) * (1. - gold * exp(-max(1. - d, 0.) / .077) * .5 * lift);
      k = mix(k, vec3(1.), .6 * exp(-dot(hp, hp) / .03));
      b = mix(b, k, clamp(uBrand - 1., 0., 1.));
    }
    if (uPearl > .001) {
      // The agent bots, white: a soft pearl that greys toward its rim and
      // foot so it holds its shape on a white page, with the same gloss.
      vec3 w = mix(uPearlTint, uPearlTint * mix(vec3(1.), uPearlEdge, .4), smoothstep(.6, 1., g));
      w *= 1. - .035 * clamp(.5 + .5 * py, 0., 1.);
      w = mix(w, uPearlTint * mix(vec3(1.), uPearlEdge, .7), .15 * pow(1. - abs(n.z), 3.)); // a faint rim
      // A crisp edge, not a blur, so it holds its shape on the page.
      w = mix(w, uPearlTint * uPearlEdge, smoothstep(.88, 1., g));
      w = mix(w, vec3(1.), .1 * up * up);
      w = mix(w, vec3(1.), .8 * exp(-dot(hp, hp) / .03));
      b = mix(b, w, uPearl);
    }
    if (uWobble > 0. && uWhirl.z < .5) {
      // A soft, slowly moving blob of an edge instead of a perfect circle:
      // the rim pulls in by a few percent, by two octaves of noise round the
      // circle that drift with time (wrapped onto the noise's 64-cell period
      // so a long-open tab stays smooth), anti-aliased over about a pixel.
      vec2 rim = q / max(d, 1e-4);
      float wt = uTexT;
      float n = .65 * vnoise(rim * 1.6 + vec2(mod(.21 * wt, 64.), 3.1 + uTex.w)) + .35 * vnoise(rim * 3.2 + vec2(7.7 + uTex.w, mod(.33 * wt, 64.)));
      // Stretch the noise's contrast so the lobes read: at full wobble the
      // rim swells and dips between 5% and 14% in from the circle.
      float edge = 1. - uWobble * (.05 + .09 * smoothstep(.2, .8, n));
      float aa = max(fwidth(d), 1e-3) * 1.2;
      alpha = 1. - smoothstep(edge - aa, edge + aa, d);
      // Clear pixels write no depth, so nothing behind them is hidden.
      if (alpha <= 0.) discard;
    }
    // Eyes stay white, or go graphite on the white orb.
    c = step(.5, uWhirl.z) > .5 ? mix(vec3(.99), uPearlEye, uPearl) : b;
  }
  float r = length(gl_FragCoord.xy - uTint.xy) / uTint.z;
  c = mix(c, warm(.42 + .7 * r), uTint.w * (1. - smoothstep(.3, .78, r)));
  // Premultiplied: a wobbly edge fades to transparent, not to white.
  o = vec4(c * alpha, alpha);
}`;

// An afterimage: a flat, soft-edged copy in one colour of the spectrum. The
// ghost is warm instead: yellow at the heart of the object, violet at its
// far tips.
const ECHO = `#version 300 es
precision highp float;
in vec3 vN, vO;
in float vS;
uniform float uGhost, uAlpha, uSat, uErode, uDev;
uniform vec3 uHeart, uC, uRim; // heart: x, y on screen and the size of one unit there, in px
out vec4 o;
${WARM}
void main() {
  vec3 c = uGhost > .5 ? warm(uDev * length(gl_FragCoord.xy - uHeart.xy) / uHeart.z) : mix(uC, uRim, smoothstep(.55, 1., vS));
  c = mix(vec3(1., .96, 1.), c, uSat);
  float edge = mix(1.5, .25, uErode);
  float a = uAlpha * (1. - smoothstep(edge - .45, edge, vS));
  o = vec4(c * a, a);
}`;

const QUAD = `#version 300 es
out vec2 vUv;
void main() { vUv = vec2(gl_VertexID & 1, gl_VertexID >> 1); gl_Position = vec4(vUv * 2. - 1., 0., 1.); }`;
const BLUR = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uTex;
uniform vec2 uDir;
out vec4 o;
void main() {
  vec4 s = vec4(0.); float w = 0.;
  for (int i = -6; i <= 6; i++) {
    float k = exp(-.5 * float(i * i) / 4.84);
    s += k * texture(uTex, vUv + uDir * float(i)); w += k;
  }
  o = s / w;
}`;
const COPY = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uTex;
uniform float uAlpha;
out vec4 o;
void main() { o = texture(uTex, vUv) * uAlpha; }`;

export class Renderer {
  constructor(canvas) {
    const gl = (this.gl = canvas.getContext("webgl2", {
      antialias: true,
      alpha: true,
      premultipliedAlpha: true,
    }));
    if (!gl) throw new Error("WebGL2 is not available");
    this.live = this.program(VERT, LIVE);
    this.echo = this.program(VERT, ECHO);
    this.blur = this.program(QUAD, BLUR);
    this.copy = this.program(QUAD, COPY);
    this.sets = new Map();
    // Each mesh's buffers on this renderer's context. Meshes are shared
    // between renderers (a renderer rebuilt after a lost context uploads its
    // own), so their VAOs can't live on the mesh.
    this.vaos = new WeakMap();
    this.bg = [0, 0, 0, 0]; // transparent: the orb sits on whatever surface holds it
    this.brand = 1; // 1: the Ask Opus fill; 2: graphite (with whirl = 1); between: a blend; 0: the experiment's sky blue
    this.pearl = 0; // 0..1: blends whatever brand drew toward the white agent orb
    this.pearlTint = [1, 1, 1]; // the pearl's body colour, 0..1 rgb
    this.pearlEdge = [0.86, 0.865, 0.89]; // its edge, as a factor of the body
    this.pearlEye = [0.16, 0.16, 0.19]; // its eyes, 0..1 rgb
    this.fill = null; // { core, mid, edge, deep } 0..1 rgb; null: Ask Opus's own
    this.tex = null; // { amount: [grain, mottle, specks, seed], speck: rgb }; null: smooth
    this.texTime = 0; // seconds; drifts the texture
    this.wobble = 0; // 0: a round edge; up to 1: a slowly moving blob of an edge
  }
  program(vs, fs) {
    const gl = this.gl,
      p = gl.createProgram();
    for (const [type, src] of [
      [gl.VERTEX_SHADER, vs],
      [gl.FRAGMENT_SHADER, fs],
    ]) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
        throw new Error(gl.getShaderInfoLog(s));
      gl.attachShader(p, s);
    }
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS))
      throw new Error(gl.getProgramInfoLog(p));
    const u = {},
      n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < n; i++) {
      const name = gl.getActiveUniform(p, i).name;
      u[name] = gl.getUniformLocation(p, name);
    }
    return { p, u };
  }
  upload(mesh) {
    const known = this.vaos.get(mesh);
    if (known) return known;
    const gl = this.gl;
    const vao = gl.createVertexArray();
    this.vaos.set(mesh, vao);
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, mesh.data, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 28, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 28, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, 28, 24);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.idx, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    return vao;
  }
  // Three buffers the size of one tile: the trail, and two half-size for the blur.
  // Kept per size, so a page can mix large and small tiles.
  targets(size) {
    if (this.sets.has(size)) return Object.assign(this, this.sets.get(size));
    const gl = this.gl;
    const make = (w, depth) => {
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA8,
        w,
        w,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null,
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        tex,
        0,
      );
      if (depth) {
        const rb = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, w);
        gl.framebufferRenderbuffer(
          gl.FRAMEBUFFER,
          gl.DEPTH_ATTACHMENT,
          gl.RENDERBUFFER,
          rb,
        );
      }
      return { tex, fbo, w };
    };
    const half = Math.max(2, size >> 1);
    this.sets.set(size, { A: make(size, true), B: make(half), C: make(half) });
    Object.assign(this, this.sets.get(size));
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
  begin(width, height) {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.disable(gl.SCISSOR_TEST);
    gl.viewport(0, 0, width, height);
    gl.clearColor(...this.bg);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    this.height = height;
  }
  drawPart(prog, part, pose) {
    const gl = this.gl,
      vao = this.upload(part.mesh);
    gl.uniformMatrix4fv(prog.u.uPart, false, pose.m || m4.I());
    gl.uniform2fv(prog.u.uBend, pose.bend || [0, 0]);
    gl.bindVertexArray(vao);
    gl.drawElements(gl.TRIANGLES, part.mesh.count, gl.UNSIGNED_SHORT, 0);
  }
  drawLive(obj, part, P) {
    const gl = this.gl,
      u = this.live.u,
      g = part.grad || obj.grad || [-0.65, 0.1];
    gl.uniform3f(u.uGrad, g[0], g[1], part.tone || 0);
    gl.uniform4fv(u.uCore, part.core || obj.core || [0, 0, 1, 0]);
    gl.uniform4fv(u.uGlow, part.glow || obj.glow || [0, 0, 1, 0]);
    gl.uniform1f(u.uBrand, this.brand ?? 0);
    gl.uniform1f(u.uPearl, this.pearl ?? 0);
    gl.uniform3fv(u.uPearlTint, this.pearlTint ?? [1, 1, 1]);
    gl.uniform3fv(u.uPearlEdge, this.pearlEdge ?? [0.86, 0.865, 0.89]);
    gl.uniform3fv(u.uPearlEye, this.pearlEye ?? [0.16, 0.16, 0.19]);
    const fill = this.fill ?? ASK_OPUS_FILL;
    gl.uniform3fv(u.uFillCore, fill.core);
    gl.uniform3fv(u.uFillMid, fill.mid);
    gl.uniform3fv(u.uFillEdge, fill.edge);
    gl.uniform3fv(u.uFillDeep, fill.deep);
    gl.uniform4fv(u.uTex, this.tex?.amount ?? [0, 0, 0, 0]);
    gl.uniform3fv(u.uSpeck, this.tex?.speck ?? [1, 1, 1]);
    gl.uniform1f(u.uTexT, this.texTime ?? 0);
    // Only the body and its dots wobble: the "!" bar and the eyes keep
    // their shape (the bar's disc is far smaller than the bar).
    gl.uniform1f(
      u.uWobble,
      part.kind === "head" || part.kind === "dot" ? this.wobble ?? 0 : 0,
    );
    gl.uniform3f(
      u.uWhirl,
      this.whirl ?? 0,
      this.whirlPhase ?? 0,
      part.kind === "eye" ? 1 : 0,
    );
    gl.uniform2f(
      u.uSpin,
      P.lon || 0,
      Math.min(1, Math.abs(Math.sin((P.lon || 0) / 2)) * 3),
    );
    {
      const m = part.pose ? part.pose(P).m || m4.I() : m4.I();
      gl.uniform3f(
        u.uDisc,
        m[12],
        m[13],
        Math.max(1e-4, Math.hypot(m[0], m[1]) * (part.discR ?? 0.72)),
      );
    }
    this.drawPart(this.live, part, part.pose ? part.pose(P) : {});
  }
  // Draw one object at loop time t into a square of `size` px at (x, y) from the top left.
  draw(obj, t, x, y, size) {
    const tr = obj.trail;
    this.drawPose(
      obj,
      {
        P: obj.params(t),
        rest: obj.params(-1),
        past: obj.params(t - tr.delay),
        since: t - tr.on,
        gone: t - tr.off,
      },
      x,
      y,
      size,
    );
  }
  // The same, from a pose worked out elsewhere (a state machine, say):
  //   P      the numbers now          rest   the pose the ghost holds
  //   past   the numbers a moment ago since  seconds since the trail came on
  //   gone   seconds since it began to leave (negative while it holds)
  //   moving which parts trail (defaults to each part's own flag)
  //   fan    the set of afterimages (defaults to the object's)
  //   under  the trail lies behind the object instead of over it, so the
  //          object stays in view (something shrinking inside its own ghost)
  drawPose(
    obj,
    { P, rest, past, since, gone, moving, fan: echoes, under },
    x,
    y,
    size,
  ) {
    const gl = this.gl;
    const moves = moving || ((part) => part.moving);
    const proj = m4.mul(m4.persp(rad(21), 1, 1, 20), m4.T(0, 0, -6.5));
    const root = obj.root(P);
    const setScene = (prog) => {
      gl.useProgram(prog.p);
      gl.uniformMatrix4fv(prog.u.uProj, false, proj);
      gl.uniformMatrix4fv(prog.u.uRoot, false, root);
    };
    const screen = () => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(x, this.height - y - size, size, size);
      gl.scissor(x, this.height - y - size, size, size);
      gl.enable(gl.SCISSOR_TEST);
    };
    const solid = () => {
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LEQUAL);
      gl.depthMask(true);
      gl.enable(gl.CULL_FACE);
      gl.disable(gl.BLEND);
    };

    // Where the heart of the object lands in a square of w px, and how big a unit is there.
    const heartAt = (rootM, w) => {
      const h = obj.heart || [0, 0, 0],
        m = m4.mul(proj, rootM);
      const cw = m[3] * h[0] + m[7] * h[1] + m[11] * h[2] + m[15];
      return [
        (((m[0] * h[0] + m[4] * h[1] + m[8] * h[2] + m[12]) / cw) * 0.5 + 0.5) *
          w,
        (((m[1] * h[0] + m[5] * h[1] + m[9] * h[2] + m[13]) / cw) * 0.5 + 0.5) *
          w,
        (proj[5] / cw) * 0.5 * w * (obj.radius || 1),
      ];
    };

    const fadeIn = smooth(since, 0, 0.13);
    const air = fadeIn * (1 - smooth(gone, 0, 0.62));
    const cling =
      0.8 * smooth(gone, 0.08, 0.36) * (1 - smooth(gone, 0.44, 0.62));
    const trail = air > 0.002;

    // 1. the trail, into a buffer of its own
    if (trail) {
      this.targets(size);
      const A = this.A,
        B = this.B,
        C = this.C;
      gl.disable(gl.SCISSOR_TEST);
      gl.bindFramebuffer(gl.FRAMEBUFFER, A.fbo);
      gl.viewport(0, 0, A.w, A.w);
      gl.clearColor(0, 0, 0, 0);
      solid();
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      // Parts that stay put hide the afterimages behind them.
      setScene(this.live);
      gl.colorMask(false, false, false, false);
      for (const part of obj.parts)
        if (!moves(part)) this.drawLive(obj, part, P);
      gl.colorMask(true, true, true, true);
      setScene(this.echo);
      gl.depthMask(false);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      const u = this.echo.u;
      // It arrives pale and warm, then deepens toward violet at the tips.
      gl.uniform1f(u.uSat, mix(0.4, 1, smooth(since, 0.02, 0.26)));
      gl.uniform1f(u.uDev, mix(0.7, 1, smooth(since, 0.1, 0.6)));
      for (const e of echoes || obj.fan || ECHOES) {
        const out = e.ghost
          ? 1
          : 1 - smooth(gone, e.fade[0], e.fade[0] + e.fade[1]);
        if (out <= 0) continue;
        const Pk = {};
        for (const k in rest) Pk[k] = mix(rest[k], past[k], e.gain);
        const rootK = obj.root(Pk);
        gl.uniformMatrix4fv(u.uRoot, false, rootK);
        gl.uniform1f(u.uGhost, e.ghost ? 1 : 0);
        if (e.ghost) {
          gl.uniform3fv(u.uHeart, heartAt(rootK, A.w));
        } else {
          gl.uniform3fv(u.uC, e.c);
          gl.uniform3fv(u.uRim, e.rim);
        }
        gl.uniform1f(u.uAlpha, out);
        gl.uniform1f(u.uErode, e.ghost ? 0.6 * smooth(gone, 0, 0.55) : 0);
        for (const part of obj.parts)
          if (moves(part)) this.drawPart(this.echo, part, part.pose(Pk));
      }
      gl.depthMask(true);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.BLEND);
      gl.disable(gl.CULL_FACE);
      // Soften it: a little while it holds, more as it leaves.
      const sigma = mix(0.0055, 0.016, smooth(gone, 0, 0.4)) / 2.2;
      gl.useProgram(this.blur.p);
      gl.bindVertexArray(null);
      gl.activeTexture(gl.TEXTURE0);
      gl.uniform1i(this.blur.u.uTex, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, B.fbo);
      gl.viewport(0, 0, B.w, B.w);
      gl.bindTexture(gl.TEXTURE_2D, A.tex);
      gl.uniform2f(this.blur.u.uDir, sigma, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.bindFramebuffer(gl.FRAMEBUFFER, C.fbo);
      gl.bindTexture(gl.TEXTURE_2D, B.tex);
      gl.uniform2f(this.blur.u.uDir, 0, sigma);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    const lay = () => {
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.CULL_FACE);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.useProgram(this.copy.p);
      gl.bindVertexArray(null);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.C.tex);
      gl.uniform1i(this.copy.u.uTex, 0);
      gl.uniform1f(this.copy.u.uAlpha, air);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.disable(gl.BLEND);
    };

    // 2. the object itself, with the trail under it or over it. As the trail
    // leaves, some of its colour clings to the moving parts for a moment.
    const hl = heartAt(root, size);
    screen();
    gl.depthMask(true);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    if (trail && under) lay();
    solid();
    setScene(this.live);
    for (const part of obj.parts) {
      gl.uniform4f(
        this.live.u.uTint,
        x + hl[0],
        this.height - y - size + hl[1],
        hl[2],
        moves(part) ? cling : 0,
      );
      this.drawLive(obj, part, P);
    }
    if (trail && !under) {
      lay();
      // parts that stay crisp on top of the trail
      solid();
      setScene(this.live);
      gl.uniform4f(this.live.u.uTint, 0, 0, 1, 0);
      for (const part of obj.parts) if (part.top) this.drawLive(obj, part, P);
    }
    gl.disable(gl.SCISSOR_TEST);
  }
}
